import { ColDef } from 'ag-grid-community';
import { Button, Modal, message } from 'antd';
import Icon from 'antd/lib/icon';
import { noop } from 'lodash/fp';
import React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controlled as CodeMirror } from 'react-codemirror2';

import { ReactComponent as ClipboardIcon } from 'assets/icons/copy-clipboard.svg';
import AgTable from 'components/AgTable';
import { useLayoutContext } from 'pages/LayoutContext';
import { getCodeMirrorOptions } from 'utils/CodeMirrorUtil';
import { tc } from 'utils/i18nUtil';
import { copyStringToClipboard } from 'utils/StringUtil';

import './JsonRendererPopover.scss';

const JSON_RENDERER_MODAL_WIDTH = 900;
const JSON_RENDERER_MODAL_MIN_HEIGHT = 220;
const JSON_RENDERER_MODAL_MAX_HEIGHT = 600;

export const StructuredJsonCellRenderer = ({
  value,
  title,
  subtitle,
}: {
  value: unknown;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
}) => <JsonRendererPopover jsonString={value as any} title={title} subtitle={subtitle} />;

export const JsonRendererPopover = ({
  jsonString: rawJsonString,
  format = 'json',
  title,
  subtitle,
  getPopupContainer,
}: {
  jsonString: string;
  format?: 'table' | 'json';
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
}) => {
  const [visible, setVisible] = useState(false);

  // Normalize: handle non-string values (e.g. objects passed directly)
  const jsonString =
    rawJsonString == null ? '' : typeof rawJsonString === 'string' ? rawJsonString : JSON.stringify(rawJsonString);

  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setVisible(true);
  }, []);

  const stopPropagation = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  }, []);

  if (!jsonString) {
    return <span className="input-output-renderer input-output-renderer--empty">—</span>;
  }

  return (
    <>
      <a className="input-output-renderer" onClick={handleOpen} onMouseDown={stopPropagation}>
        {tc('view')}
      </a>
      <Modal
        title={
          title || subtitle ? (
            <div className="json-renderer-modal-header">
              <div className="json-renderer-modal-title">
                {title ? <div className="json-renderer-modal-title__label">{title}</div> : null}
                {subtitle ? <div className="json-renderer-modal-title__meta">{subtitle}</div> : null}
              </div>
              <button
                type="button"
                className="json-renderer-modal-header__close"
                onClick={() => setVisible(false)}
                aria-label={tc('close')}>
                <Icon type="close" />
              </button>
            </div>
          ) : null
        }
        visible={visible}
        onCancel={() => setVisible(false)}
        closable={false}
        footer={[
          <Button key="copy" onClick={() => copyToClipboard(jsonString)}>
            <span className="json-renderer-modal-copy-button">
              <ClipboardIcon width={16} height={16} /> <span>Copy JSON</span>
            </span>
          </Button>,
          <Button key="close" onClick={() => setVisible(false)}>
            {tc('close')}
          </Button>,
        ]}
        destroyOnClose
        width={JSON_RENDERER_MODAL_WIDTH}
        className="json-renderer-modal"
        bodyStyle={{ padding: 0, overflow: 'hidden' }}
        getContainer={getPopupContainer ? ((getPopupContainer as unknown) as () => HTMLElement) : undefined}>
        {format === 'table' ? (
          <TableRenderer jsonString={jsonString} />
        ) : (
          <CodeMirrorRenderer jsonString={jsonString} />
        )}
      </Modal>
    </>
  );
};

interface RendererProps {
  jsonString: string;
}

const TableRenderer = ({ jsonString }: RendererProps) => {
  let records: {
    header: string;
    value: any;
  }[] = [];
  try {
    const json = JSON.parse(jsonString);
    records = Object.keys(json).map((key) => {
      const rawValue = json[key];
      return {
        header: key,
        value: typeof rawValue === 'object' ? JSON.stringify(rawValue) : rawValue,
      };
    });
  } catch (err) {
    // If not valid JSON, show the raw string via copy button only
    console.log('error', err);
  }

  const colDefs = useMemo<ColDef[]>(() => {
    return [
      {
        headerName: tc('header'),
        colId: 'header',
        field: 'header',
      },
      {
        headerName: tc('value'),
        colId: 'value',
        field: 'value',
      },
    ];
  }, []);

  return (
    <div className="popover-table-renderer">
      <AgTable
        suppressCellSelection
        className={'custom-synapse__table'}
        domLayout="autoHeight"
        columnDefs={colDefs}
        rowData={records}
      />
    </div>
  );
};

const CodeMirrorRenderer = ({ jsonString }: RendererProps) => {
  const [editor, setEditor] = useState<CodeMirror.Editor | null>(null);
  const layout = useLayoutContext();

  const updateSize = useCallback((editor?: CodeMirror.Editor) => {
    if (!editor) {
      return;
    }
    const lineCount = editor.getDoc().lineCount();
    const editorHeight = Math.min(
      JSON_RENDERER_MODAL_MAX_HEIGHT,
      Math.max(JSON_RENDERER_MODAL_MIN_HEIGHT, lineCount * 20)
    );
    editor.setSize(null, editorHeight);
  }, []);

  useEffect(() => {
    if (editor) {
      updateSize(editor);
      setTimeout(() => editor.refresh(), 0);
    }
  }, [editor, layout.dimensions.content.height, updateSize]);

  let formatedJsonString = jsonString;
  try {
    formatedJsonString = JSON.stringify(JSON.parse(jsonString), undefined, 4);
  } catch (err) {
    // If not valid JSON, show the raw string as-is
  }

  return (
    <CodeMirror
      className="code-mirror-container"
      value={formatedJsonString}
      options={getCodeMirrorOptions(true)}
      editorDidMount={(editor: CodeMirror.Editor) => {
        setEditor(editor);
        updateSize(editor);
        setTimeout(() => editor.refresh(), 0);
      }}
      onBeforeChange={noop}
    />
  );
};

const copyToClipboard = async (textToCopy: string) => {
  let formattedText = textToCopy;
  try {
    formattedText = JSON.stringify(JSON.parse(textToCopy), undefined, 4);
  } catch (err) {
    // copy raw string
  }
  try {
    await copyStringToClipboard(formattedText);
    message.success(tc('copy_to_clipboard_success'));
  } catch (err) {
    message.error(tc('copy_to_clipboard_failure'));
  }
};
